import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../../loading-spinner';
import ShowSigningPrepMessageModal from './ShowSigningPrepMessageModal';
import { getDocumentById } from '../../../api/queries_v2';
import { useNotifications } from '../../../notifications';

type ActivityPropsType = {
  id: string;
  description?: string;
  time?: string;
  actor_data: {
    full_name: string;
    email: string;
  };
  object_data: {
    object_id: string;
  };
  verb: string;
};

type ShowSigningPrepMessageModalProps = {
  isOpen: boolean;
  activity: ActivityPropsType | null;
  onClose: () => void;
};

const ShowSigningPrepMessageModalContainer = ({ isOpen, onClose, activity }: ShowSigningPrepMessageModalProps) => {
  const [, { error: notifyError }] = useNotifications();
  const {
    data: documentData,
    loading: documentDataLoading,
    error: documentDataError,
  } = useQuery(getDocumentById, {
    variables: { documentId: activity?.object_data?.object_id! },
    skip: !activity?.object_data?.object_id,
  });

  useEffect(() => {
    if (documentDataError && isOpen) {
      const errorMessage = documentDataError.message || '';
      if (errorMessage.includes("Couldn't find Document")) {
        notifyError({
          message: {
            id: 'document-not-found-error',
            defaultMessage: 'Document cannot be found',
          },
        });
        onClose();
      }
    }
  }, [documentDataError, isOpen, notifyError, onClose]);

  if (documentDataLoading) {
    return <LoadingSpinner />;
  }

  if (documentDataError) {
    return null;
  }

  if (!activity) {
    return null;
  }

  // Extract the actual activity data
  const actionText =
    activity.verb === 'document-signing-preparation-started'
      ? 'Sent for Signing Preparation'
      : activity.verb === 'document-signing-preparation-canceled'
        ? 'Canceled Signing Preparation'
        : 'Unknown Action';

  const messageData = {
    message:
      (activity as any).additional_message || `No message was provided with this ${actionText.toLowerCase()} request.`,
    sentBy: activity.actor_data?.full_name || 'Unknown User',
    sentAt: activity.time || new Date().toISOString(),
    documentTitle:
      documentData?.getDocumentById?.title ||
      activity.description
        ?.replace(' sent for signing preparation', '')
        .replace(' signing preparation cancelled', '') ||
      'Unknown Document',
    actionText: actionText,
  };

  return <ShowSigningPrepMessageModal isOpen={isOpen} onClose={onClose} messageData={messageData} />;
};

export default ShowSigningPrepMessageModalContainer;
